import type { VercelProject, VercelRunner } from '@cli-types/next-adapter/vercel.js'
import { createAndLinkProject } from './create-and-link-project.js'
import { sanitizeVercelProjectName } from './sanitize-vercel-project-name.js'

/**
 * Reusing an existing project of the same name is avoided: a reused project usually carries a
 * `DATABASE_URL` env var from a previous run, which makes the Neon integration's connect step fail
 * with a 400. Existence is probed with the read-only `project inspect` (exit 0 = taken) because
 * v54+ CLIs make `projects add` an idempotent success on existing names; the "already exists" error
 * only catches conflicts on older CLIs.
 *
 * Scope handling: the first pass passes `options.scope` (the `whoami` username) because Vercel CLI
 * 56.2.1+ in non-TTY mode fails with `missing_scope` on multi-scope accounts unless a scope is
 * explicit. But on team-first accounts (every account created since mid-2024), projects can only
 * live in a team — `whoami` still prints the bare username, which is not a project-owning scope
 * there, so every `--scope <username>` command fails; hence the second, unscoped pass.
 */
export async function createVercelProject(
  runner: VercelRunner,
  cwd: string,
  name: string,
  options: { env?: NodeJS.ProcessEnv; scope?: string } = {}
): Promise<VercelProject> {
  const base = sanitizeVercelProjectName(name)

  const scoped = await createAndLinkProject(runner, cwd, base, options)
  if (scoped.linked || !options.scope) return scoped

  return createAndLinkProject(runner, cwd, base, {
    env: options.env,
    preferredScope: options.scope
  })
}
